import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Col, Container, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  DeleteConfirmation,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS } from '../../common/constants';
import { initialTranslationsState } from '../../common/types/translations.types';
import {
  deletePlaceAsync,
  getPlaceDetailsAsync,
  getPlacesAsync,
  PlaceDetails,
  savePlaceAsync,
  selectPlaceDetails,
  selectPlaceDetailsLoadingStatus,
  selectPlaceItems,
  updatePlaceAsync,
} from './placesSlice';
import {
  getMunicipalitiesAsync,
  selectMunicipalityItems,
} from '../municipalities/municipalitiesSlice';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';

function PlaceAddOrUpdate() {
  const backLinkUrl = '/locations/places';
  const params = useParams();
  const dispatch = useAppDispatch();
  const placeDetails = useAppSelector(selectPlaceDetails);
  const municipalities = useAppSelector(selectMunicipalityItems);
  const places = useAppSelector(selectPlaceItems);
  const placeDetailsLoadingStatus = useAppSelector(
    selectPlaceDetailsLoadingStatus,
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const isAddMode = !params.placeId;

  const initialPlaceState: PlaceDetails = {
    nameTranslations: initialTranslationsState,
    descriptionTranslations: initialTranslationsState,
    latitude: '',
    longitude: '',
    municipalityId: '',
    parentPlaceId: '',
    showSearchSuggestion: false,
  };

  const [place, setPlace] = useState(initialPlaceState);
  const [placeNameFi, setPlaceNameFi] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleted, setDeleted] = useState(false);

  const municipalityOptions = municipalities.map(municipality => {
    return { value: municipality.id, label: municipality.name };
  });

  const placeOptions = places
    .filter(p => !place?.id || p.id !== place.id)
    .map(p => ({ value: p.id, label: p.name }));

  const initFetch = useCallback(() => {
    dispatch(
      getMunicipalitiesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
      }),
    );

    dispatch(
      getPlacesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
      }),
    );

    // Fetch place details only when editing (placeId exists)
    if (params.placeId) {
      dispatch(getPlaceDetailsAsync(params.placeId));
    }
  }, [dispatch, params.placeId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  useEffect(() => {
    if (!isAddMode) {
      setPlace(placeDetails);
    }
  }, [isAddMode, placeDetails]);

  useEffect(() => {
    if (place.nameTranslations.length > 0) {
      const placeObject = place.nameTranslations.find(
        obj => obj.locale === 'fi_FI',
      );
      setPlaceNameFi(placeObject.text);
    }
  }, [place]);

  const deletePlace = () => {
    dispatch(deletePlaceAsync(params.placeId));
    setDeleted(true);
    setShowDeleteModal(false);
  };

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newPlace = { ...place };
    let pointer = newPlace;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setPlace(newPlace);
  };

  const handleTranslation = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { culture } = event.target.dataset;
    const textValue = event.target.value;
    const newPlace = { ...place };

    newPlace[finalProp] = place[finalProp].map(item => {
      if (item.locale === culture) {
        return { ...item, text: textValue };
      }
      return item;
    });
    setPlace(newPlace);
  };

  const handleSelectChange = (selectedOption, name) => {
    const newPlace = { ...place };
    newPlace[name] = selectedOption ? selectedOption.value : '';
    setPlace(newPlace);
  };

  const addOrUpdatePlace = async () => {
    let actionResult: Action<string>;
    if (isAddMode) {
      actionResult = await dispatch(savePlaceAsync(place));
    } else {
      actionResult = await dispatch(updatePlaceAsync(place));
    }

    if (
      savePlaceAsync.fulfilled.match(actionResult) ||
      updatePlaceAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Maan ${placeNameFi} tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const generatePageTitle = function () {
    if (isAddMode) {
      return 'Uuden paikan lisäys';
    }
    if (
      placeDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      placeNameFi !== ''
    ) {
      return `Paikan tietojen päivitys: ${placeNameFi}`;
    }
    return 'Paikan tietojen päivitys';
  };

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Paikat</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Sijaintitiedot', 'Paikat']}
          Title={generatePageTitle()}
        />

        <Loader
          IsLoading={placeDetailsLoadingStatus === LOADING_STATUS.LOADING}
        />

        {placeDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            {place.nameTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="nameTranslations"
                className="mb-40"
              >
                <Form.Label>Nimi ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="nameTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Form.Group className="mb-40">
              <Form.Label>Kunta</Form.Label>
              <Select
                inputId="municipalityId"
                name="municipalityId"
                placeholder="Valitse kunta"
                isSearchable
                options={municipalityOptions}
                onChange={option =>
                  handleSelectChange(option, 'municipalityId')
                }
                value={municipalityOptions.filter(
                  option => option.value === place.municipalityId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <Form.Group className="mb-40">
              <Form.Label>Paikka ylätasolla</Form.Label>
              <Select
                inputId="parentPlaceId"
                name="parentPlaceId"
                placeholder="Valitse vapaavalintainen paikka ylätasolla"
                isSearchable
                isClearable
                options={placeOptions}
                onChange={option => handleSelectChange(option, 'parentPlaceId')}
                value={placeOptions.filter(
                  option => option.value === place.parentPlaceId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <hr className="hr-light mb-32" />

            {place.descriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="descriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Kuvaus ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="descriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Row xs={2}>
              <Col>
                <Form.Group controlId="latitude">
                  <Form.Label>Leveysaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={place.latitude}
                    onChange={handleInputChange}
                    name="latitude"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="longitude">
                  <Form.Label>Pituusaste</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={place.longitude}
                    onChange={handleInputChange}
                    name="longitude"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="hr-light mb-32" />

            <Form.Group controlId="showSearchSuggestion" className="mb-40">
              <Form.Check
                type="checkbox"
                label="Näytä hakuehdotus haussa"
                name="showSearchSuggestion"
                checked={!!place.showSearchSuggestion}
                onChange={handleInputChange}
              />
            </Form.Group>

            <InputErrorsDisplay inputErrors={inputErrors} />

            <ActionButtons
              CancelButtonText={
                deleted || submitted ? 'Palaa takaisin' : 'Peruuta'
              }
              CancelButtonUrl={backLinkUrl}
              DeleteButtonText={isAddMode || deleted ? '' : 'Poista alue'}
              HandleDelete={
                isAddMode || deleted ? null : () => setShowDeleteModal(true)
              }
              HandleSave={addOrUpdatePlace}
            />

            {submitted && <Alert variant="success">Tiedot tallennettu!</Alert>}
            {(place.isDeleted || deleted) && (
              <Alert variant="danger">Alue on poistettu!</Alert>
            )}
          </Container>
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={deletePlace}
        HideModal={() => setShowDeleteModal(false)}
        Id={place.id}
        Message={`Haluatko varmasti poistaa alueen ${placeNameFi}?`}
        ShowModal={showDeleteModal}
      />
    </>
  );
}

export default PlaceAddOrUpdate;
